import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  archiveAfterSalesGroupV2,
  createAfterSalesIssueLegacy,
  createAfterSalesIssueV2,
  decideAfterSalesReview,
  fetchAfterSalesHistory,
  fetchAfterSalesReviewDetail,
  fetchAfterSalesGroup,
  fetchAfterSalesGroups,
  startAfterSalesGroupSyncV2,
  updateAfterSalesIssue,
} from '@/services/api/afterSales'
import type {
  AfterSaleGroupSummary,
  AfterSaleIssueCreatePayload,
  AfterSaleIssueUpdatePayload,
  AfterSaleReviewDecisionPayload,
} from '@/types/afterSales'

const REFRESH_INTERVAL_MS = 30_000

export function useAfterSalesGroups(params: {
  search?: string
  sort?: 'updated_desc' | 'issues_desc' | 'name_asc'
  serviceOwner?: string
  salesOwner?: string
  page: number
  pageSize: number
}) {
  return useQuery({
    queryKey: ['after-sales', 'groups', params],
    queryFn: () => fetchAfterSalesGroups(params),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    gcTime: 0,
    refetchInterval: REFRESH_INTERVAL_MS,
  })
}

export function useAfterSalesGroup(
  roomId: string | null,
  summary?: AfterSaleGroupSummary,
  enabled = true,
) {
  return useQuery({
    queryKey: ['after-sales', 'group', roomId, summary?.authorityBackend],
    queryFn: () => fetchAfterSalesGroup(roomId!, summary),
    enabled: enabled && Boolean(roomId),
    staleTime: 15_000,
    gcTime: 0,
    refetchInterval: REFRESH_INTERVAL_MS,
  })
}

export function useAfterSalesHistory(roomId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['after-sales', 'history', roomId],
    queryFn: () => fetchAfterSalesHistory(roomId!),
    enabled: enabled && Boolean(roomId),
    // The V2 version ledger is a slow cold read upstream, so keep the result around
    // instead of dropping it on unmount.
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
}

export function useAfterSalesReviewDetail(
  roomId: string | null,
  reviewId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['after-sales', 'review', roomId, reviewId],
    queryFn: () => fetchAfterSalesReviewDetail(roomId!, reviewId!),
    enabled: enabled && Boolean(roomId && reviewId),
    staleTime: 0,
    gcTime: 0,
  })
}

export function useDecideAfterSalesReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      roomId,
      reviewId,
      payload,
    }: {
      roomId: string
      reviewId: string
      payload: AfterSaleReviewDecisionPayload
    }) => decideAfterSalesReview(roomId, reviewId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'groups'] })
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'group', variables.roomId] })
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'review', variables.roomId, variables.reviewId] })
    },
  })
}

export function useUpdateAfterSalesIssue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      roomId,
      issueId,
      payload,
    }: {
      roomId: string
      issueId: string
      payload: AfterSaleIssueUpdatePayload
    }) => updateAfterSalesIssue(roomId, issueId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'groups'] })
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'group', variables.roomId] })
    },
  })
}

export function useStartAfterSalesGroupSyncV2() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (roomId: string) => startAfterSalesGroupSyncV2(roomId),
    onSuccess: (_, roomId) => {
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'group', roomId] })
    },
  })
}

export function useCreateAfterSalesIssueV2() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ roomId, payload }: { roomId: string; payload: AfterSaleIssueCreatePayload }) =>
      createAfterSalesIssueV2(roomId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'groups'] })
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'group', variables.roomId] })
    },
  })
}

export function useCreateAfterSalesIssueLegacy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ roomId, payload }: { roomId: string; payload: AfterSaleIssueCreatePayload }) =>
      createAfterSalesIssueLegacy(roomId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'groups'] })
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'group', variables.roomId] })
    },
  })
}

export function useArchiveAfterSalesGroupV2() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (roomId: string) => archiveAfterSalesGroupV2(roomId),
    onSuccess: (_, roomId) => {
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'groups'] })
      queryClient.invalidateQueries({ queryKey: ['after-sales', 'group', roomId] })
    },
  })
}
